import React from "react";
import {
  DragDropContext,
  DragDropContextProps,
  DragStart,
  Droppable,
  DroppableProps,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
  ResponderProvided
} from "react-beautiful-dnd";
import { aimDropAtPointer, findDropTargetAt, IDropPoint, IDropTargetBox } from "./pointer-drop-target";

/**
 * react-beautiful-dnd, aimed with the pointer. Drop-in replacements for `DragDropContext` and
 * `Droppable` on a surface whose droppables are stacked lists — folders, table groups — where rbd's
 * own box-overlap rule cannot reach a short list below a neighbour (see `pointer-drop-target.ts`).
 *
 * During a pointer (FLUID) drag the context hit-tests the pointer against every droppable on each
 * move and makes the one under it the only droppable rbd may land in (`isDropDisabled` on the rest,
 * which rbd supports changing mid-drag — so its gap-opening happens where the user is aiming), the
 * one whose snapshot reads `isDraggingOver`, and the destination `onDragEnd` reports, whatever rbd
 * concluded. A keyboard (SNAP) drag is rbd's alone. rbd keeps the lift, the keyboard sensor and the
 * animations. EVERY droppable under a `PointerAimedDragDropContext` must be a
 * `PointerAimedDroppable`: the context only knows the boxes of the droppables that registered.
 */

//  ────────────────────────────────────────────────────────────────────────────
//  Where the pointer is
//  ────────────────────────────────────────────────────────────────────────────

/**
 * The pointer's last viewport position, kept while any pointer-aimed context is mounted. It has to
 * be known BEFORE a drag starts: rbd lifts on a move past its 5px threshold, and a pointer that got
 * there in one jump (a fast flick, a scripted drag) may send no further move before it is released —
 * a drag aimed only from the moves after `onDragStart` would still think it is over its source.
 */
let lastPointer: IDropPoint | null = null;
let retainCount: number = 0;
const moveListeners: Set<(point: IDropPoint) => void> = new Set<(point: IDropPoint) => void>();

const notePointer = (point: IDropPoint): void => {
  lastPointer = point;
  moveListeners.forEach((listener: (moved: IDropPoint) => void): void => listener(point));
};

const onPointerEvent = (event: PointerEvent): void => notePointer({ x: event.clientX, y: event.clientY });

// A finger whose pointer stream the browser cancelled for a pan still sends touch events.
const onTouchEvent = (event: TouchEvent): void => {
  const touch: Touch | undefined = event.touches[0];
  if (touch) notePointer({ x: touch.clientX, y: touch.clientY });
};

const LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: true };

const retainPointerTracking = (): (() => void) => {
  retainCount += 1;
  if (retainCount === 1) {
    window.addEventListener("pointerdown", onPointerEvent, LISTENER_OPTIONS);
    window.addEventListener("pointermove", onPointerEvent, LISTENER_OPTIONS);
    window.addEventListener("touchstart", onTouchEvent, LISTENER_OPTIONS);
    window.addEventListener("touchmove", onTouchEvent, LISTENER_OPTIONS);
  }
  return (): void => {
    retainCount -= 1;
    if (retainCount > 0) return;
    window.removeEventListener("pointerdown", onPointerEvent, LISTENER_OPTIONS);
    window.removeEventListener("pointermove", onPointerEvent, LISTENER_OPTIONS);
    window.removeEventListener("touchstart", onTouchEvent, LISTENER_OPTIONS);
    window.removeEventListener("touchmove", onTouchEvent, LISTENER_OPTIONS);
    lastPointer = null;
  };
};

//  ────────────────────────────────────────────────────────────────────────────
//  The context
//  ────────────────────────────────────────────────────────────────────────────

interface IPointerAim {
  /** A pointer (not keyboard) drag is live. */
  isPointerDrag: boolean;
  /** The droppable under the pointer; null over none. */
  droppableId: string | null;
  draggableId: string | null;
}

interface IRegisteredDroppable {
  element: React.MutableRefObject<HTMLElement | null>;
  isDisabled: React.MutableRefObject<boolean>;
}

interface IPointerAimRegistry {
  /** Returns the unregister. */
  register: (droppableId: string, droppable: IRegisteredDroppable) => () => void;
}

const IDLE: IPointerAim = { isPointerDrag: false, droppableId: null, draggableId: null };

const PointerAimContext: React.Context<IPointerAim> = React.createContext<IPointerAim>(IDLE);
const PointerAimRegistryContext: React.Context<IPointerAimRegistry | null> =
  React.createContext<IPointerAimRegistry | null>(null);

export const PointerAimedDragDropContext = (props: DragDropContextProps): React.ReactElement => {
  const { onDragStart, onDragEnd } = props;
  const droppables: React.MutableRefObject<Map<string, IRegisteredDroppable>> = React.useRef<
    Map<string, IRegisteredDroppable>
  >(new Map<string, IRegisteredDroppable>());
  const [aim, setAim] = React.useState<IPointerAim>(IDLE);
  // The move and drop handlers outlive the render that bound them, so they read the aim from here.
  const aimRef: React.MutableRefObject<IPointerAim> = React.useRef<IPointerAim>(IDLE);
  const stopFollowingRef: React.MutableRefObject<(() => void) | null> = React.useRef<(() => void) | null>(null);

  const registry: IPointerAimRegistry = React.useMemo(
    (): IPointerAimRegistry => ({
      register: (droppableId: string, droppable: IRegisteredDroppable): (() => void) => {
        droppables.current.set(droppableId, droppable);
        return (): void => {
          if (droppables.current.get(droppableId) === droppable) droppables.current.delete(droppableId);
        };
      }
    }),
    []
  );

  React.useEffect((): (() => void) => {
    const release: () => void = retainPointerTracking();
    return (): void => {
      // A drag cut short by an unmount must not leave its listener behind.
      stopFollowingRef.current?.();
      release();
    };
  }, []);

  const setPointerAim = (next: IPointerAim): void => {
    aimRef.current = next;
    setAim(next);
  };

  /** Every droppable that can take the drop, as the viewport has it now; boxes move as rbd opens gaps. */
  const readBoxes = (): IDropTargetBox[] =>
    Array.from(droppables.current.entries()).flatMap(
      ([id, droppable]: [string, IRegisteredDroppable]): IDropTargetBox[] => {
        const element: HTMLElement | null = droppable.element.current;
        if (!element || droppable.isDisabled.current) return [];
        const box: DOMRect = element.getBoundingClientRect();
        return [{ id, top: box.top, bottom: box.bottom, left: box.left, right: box.right }];
      }
    );

  const aimAt = (point: IDropPoint): void => {
    const droppableId: string | null = findDropTargetAt(readBoxes(), point);
    if (droppableId === aimRef.current.droppableId) return;
    setPointerAim({ ...aimRef.current, droppableId });
  };

  /**
   * Re-aims on every move from the lift to the release, and once more AT the release. From then on
   * rbd animates the drop and takes no more enable changes, so the droppable aimed at then stands.
   */
  const followPointer = (): void => {
    const stop = (): void => {
      moveListeners.delete(aimAt);
      window.removeEventListener("pointerup", onPointerRelease, LISTENER_OPTIONS);
      window.removeEventListener("touchend", onTouchRelease, LISTENER_OPTIONS);
      stopFollowingRef.current = null;
    };
    const onPointerRelease = (event: PointerEvent): void => {
      aimAt({ x: event.clientX, y: event.clientY });
      stop();
    };
    const onTouchRelease = (event: TouchEvent): void => {
      const touch: Touch | undefined = event.changedTouches[0];
      if (touch) aimAt({ x: touch.clientX, y: touch.clientY });
      stop();
    };
    moveListeners.add(aimAt);
    window.addEventListener("pointerup", onPointerRelease, LISTENER_OPTIONS);
    window.addEventListener("touchend", onTouchRelease, LISTENER_OPTIONS);
    stopFollowingRef.current = stop;
  };

  const handleDragStart = (start: DragStart, provided: ResponderProvided): void => {
    if (start.mode === "FLUID") {
      // Where the pointer already is — the lift itself was a move — else the item's own list.
      const droppableId: string | null = lastPointer
        ? findDropTargetAt(readBoxes(), lastPointer)
        : start.source.droppableId;
      setPointerAim({ isPointerDrag: true, droppableId, draggableId: start.draggableId });
      followPointer();
    }
    onDragStart?.(start, provided);
  };

  const handleDragEnd = (result: DropResult, provided: ResponderProvided): void => {
    stopFollowingRef.current?.();
    const aimed: DropResult =
      result.mode === "FLUID" && aimRef.current.isPointerDrag
        ? aimDropAtPointer(result, aimRef.current.droppableId)
        : result;
    setPointerAim(IDLE);
    onDragEnd(aimed, provided);
  };

  return (
    <PointerAimRegistryContext.Provider value={registry}>
      <PointerAimContext.Provider value={aim}>
        <DragDropContext {...props} onDragStart={handleDragStart} onDragEnd={handleDragEnd} />
      </PointerAimContext.Provider>
    </PointerAimRegistryContext.Provider>
  );
};

//  ────────────────────────────────────────────────────────────────────────────
//  A droppable
//  ────────────────────────────────────────────────────────────────────────────

/** rbd's `innerRef`, also recording the element — one function per rbd ref, so React does not churn it. */
interface IInnerRefCache {
  rbdRef?: (element: HTMLElement | null) => void;
  combined?: (element: HTMLElement | null) => void;
}

export const PointerAimedDroppable = (props: DroppableProps): React.ReactElement => {
  const { droppableId, isDropDisabled, children } = props;
  const registry: IPointerAimRegistry | null = React.useContext(PointerAimRegistryContext);
  const aim: IPointerAim = React.useContext(PointerAimContext);
  const element: React.MutableRefObject<HTMLElement | null> = React.useRef<HTMLElement | null>(null);
  const isDisabled: React.MutableRefObject<boolean> = React.useRef<boolean>(isDropDisabled === true);
  const innerRefCache: React.MutableRefObject<IInnerRefCache> = React.useRef<IInnerRefCache>({});

  React.useLayoutEffect((): void => {
    isDisabled.current = isDropDisabled === true;
  }, [isDropDisabled]);

  React.useEffect(
    (): (() => void) | undefined => registry?.register(droppableId, { element, isDisabled }),
    [registry, droppableId]
  );

  const withElement = (rbdRef: (element: HTMLElement | null) => void): ((element: HTMLElement | null) => void) => {
    const cache: IInnerRefCache = innerRefCache.current;
    if (cache.rbdRef !== rbdRef || !cache.combined) {
      cache.rbdRef = rbdRef;
      cache.combined = (node: HTMLElement | null): void => {
        element.current = node;
        rbdRef(node);
      };
    }
    return cache.combined;
  };

  const isAimedAt: boolean = aim.isPointerDrag && aim.droppableId === droppableId && isDropDisabled !== true;
  const isAimedElsewhere: boolean = aim.isPointerDrag && !isAimedAt;

  return (
    <Droppable {...props} isDropDisabled={isDropDisabled === true || isAimedElsewhere}>
      {(provided: DroppableProvided, snapshot: DroppableStateSnapshot): React.ReactElement<HTMLElement> =>
        children(
          { ...provided, innerRef: withElement(provided.innerRef) },
          aim.isPointerDrag
            ? { ...snapshot, isDraggingOver: isAimedAt, draggingOverWith: isAimedAt ? aim.draggableId : null }
            : snapshot
        )
      }
    </Droppable>
  );
};
